import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2, Package, ShoppingBag, MessageSquare, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useI18n } from "../i18n";

export default function MyListingsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [data, setData] = useState({ products: [], requests: [], offers: [] });
  const [loading, setLoading] = useState(true);
  const [boosting, setBoosting] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/me/listings");
      setData(res.data);
    } catch {
      toast.error("Failed to load listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const boost = async (kind, id) => {
    setBoosting(`${kind}-${id}`);
    try {
      await api.post(`/${kind}/${id}/boost`, { days: 7 });
      toast.success("Boosted for 7 days");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBoosting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-14">
      <button
        onClick={() => navigate("/")}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        Home
      </button>

      <div className="mb-10">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
          My dashboard
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          Your listings.
        </h1>
        <p className="mt-3 text-muted-foreground">
          Boost any listing to appear as a <strong>Sponsored</strong> card at the top of every grid.
        </p>
      </div>

      <Section
        icon={<ShoppingBag className="h-4 w-4" />}
        title="Products"
        items={data.products}
        onOpen={(p) => navigate(`/p/${p.id}`)}
        onBoost={(p) => boost("products", p.id)}
        boostingKey={boosting}
        keyPrefix="products"
        testIdBase="my-product"
        emptyCta={() => navigate("/new-product")}
        emptyText="You haven't listed any products yet."
        emptyBtn="List a product"
      />

      <Section
        icon={<Package className="h-4 w-4" />}
        title="Buy requests"
        items={data.requests}
        onOpen={(r) => navigate(`/r/${r.id}`)}
        onBoost={(r) => boost("requests", r.id)}
        boostingKey={boosting}
        keyPrefix="requests"
        testIdBase="my-request"
        emptyCta={() => navigate("/new")}
        emptyText="You haven't posted any buy requests yet."
        emptyBtn="Post a request"
      />

      <Section
        icon={<MessageSquare className="h-4 w-4" />}
        title="Offers made"
        items={data.offers}
        onOpen={(o) => navigate(`/r/${o.request_id}`)}
        onBoost={(o) => boost("offers", o.id)}
        boostingKey={boosting}
        keyPrefix="offers"
        testIdBase="my-offer"
        emptyText="You haven't sent any offers yet."
      />
    </div>
  );
}

function Section({ icon, title, items, onOpen, onBoost, boostingKey, keyPrefix, testIdBase, emptyCta, emptyText, emptyBtn }) {
  return (
    <section className="mb-12">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h2 className="font-display text-xl font-semibold">
          {title} <span className="text-muted-foreground font-normal">· {items.length}</span>
        </h2>
      </div>
      {items.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-border bg-white p-8 text-sm text-muted-foreground text-center">
          {emptyText}
          {emptyCta && emptyBtn && (
            <div className="mt-3">
              <Button onClick={emptyCta} size="sm" className="rounded-md">{emptyBtn}</Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const bkey = `${keyPrefix}-${it.id}`;
            const isBoosting = boostingKey === bkey;
            return (
              <div
                key={it.id}
                data-testid={`${testIdBase}-${it.id}`}
                className="rounded-md border border-border bg-white p-4 flex items-center justify-between gap-4 hover:border-foreground/20 transition-colors"
              >
                <button
                  onClick={() => onOpen(it)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-semibold truncate">{it.title || it.description?.slice(0, 60) || "Untitled"}</div>
                    {it.is_sponsored && (
                      <div className="inline-flex items-center gap-1 rounded-full bg-accent/20 text-accent px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider">
                        <Sparkles className="h-2.5 w-2.5" fill="currentColor" />
                        Sponsored
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {it.price || it.budget || ""} · {new Date(it.created_at).toLocaleDateString()}
                  </div>
                </button>
                <Button
                  size="sm"
                  variant={it.is_sponsored ? "outline" : "default"}
                  disabled={isBoosting || it.is_sponsored}
                  onClick={() => onBoost(it)}
                  data-testid={`boost-${keyPrefix}-${it.id}`}
                  className="rounded-md gap-1.5 whitespace-nowrap"
                >
                  {isBoosting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : it.is_sponsored ? (
                    <>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Sponsored
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" /> Boost 7d
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
